import { Link, Outlet, useNavigate } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { BellIcon, MenuIcon, XIcon, CheckCheck, MoreVertical, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useUsuarioWithRole } from "../../hooks/useUsuarioWithRole";
import { useState, useEffect, useMemo } from "react";
import useNotifications from "../../hooks/useNotifications";
import NotificationCard from "../common/NotificationCard";
import coursesService from "../../services/CoursesService";
import { normalizeLocalUser, getUserProfileLabel } from "../../utils/normalizeLocalUser";

const Navbar = ({ withoutOutlet = false }) => {
  const navigate = useNavigate();
  const { logout, usuarioConCedula: usuarioFromAuth } = useAuth();
  const { isAdmin } = useUsuarioWithRole();
  const mainLogo = "/img/logocom.png";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);

  const usuarioConCedula = useMemo(() => {
    const fromAuth = normalizeLocalUser(usuarioFromAuth);
    if (fromAuth?.id) return fromAuth;
    try {
      return normalizeLocalUser(JSON.parse(localStorage.getItem("usuarioConCedula") || "null"));
    } catch {
      return null;
    }
  }, [usuarioFromAuth]);

  const userId = usuarioConCedula?.id ?? null;
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    error: notificationsError,
    isConnected,
    markAsRead,
    markAllAsRead,
    reconnect,
    toastNotification,
    dismissToast
  } = useNotifications(
    userId ? `/api/Notification/${userId}` : `/api/Notification/0`,
    `${process.env.REACT_APP_LOCATION}/notificationHub`,
    `${process.env.REACT_APP_LOCATION}`,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  useEffect(() => {
    if (notifications.length > 0 && isNotificationMenuOpen) {
      setIsNotificationMenuOpen(false);
      setTimeout(() => {
        setIsNotificationMenuOpen(true);
      }, 50);
    }
  }, [notifications.length, isNotificationMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  /** Nombre visible: cada palabra con mayúscula inicial (p. ej. "Ivan Andres Castro"). */
  const formatUserDisplayName = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    const plain = stripHtml(raw).replace(/\s+/g, " ").trim();
    if (!plain) return "";
    return plain
      .split(" ")
      .map((word) => {
        if (!word) return "";
        const lower = word.toLocaleLowerCase("es");
        return lower.charAt(0).toLocaleUpperCase("es") + lower.slice(1);
      })
      .join(" ");
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;

    markAsRead(notification.id);

    if (notification.courseId) {
      try {
        console.log('Navbar: Checking enrollment for notification courseId:', notification.courseId);
        const usuarioConCedula = normalizeLocalUser(
          JSON.parse(localStorage.getItem("usuarioConCedula") || "null")
        );
        const userCourses = await coursesService.getUserCoursesWithCedula(usuarioConCedula, navigate);
        console.log('Navbar: User courses:', userCourses.map(c => ({ id: c.id, title: c.title })));
        const isEnrolled = userCourses.some(course => course.id === parseInt(notification.courseId));
        console.log('Navbar: Enrollment result:', isEnrolled);
        
        if (isEnrolled) {
          console.log('Navbar: User enrolled, navigating to course');
          navigate(`/courses/Introcourse/${notification.courseId}`);
        } else {
          console.log('Navbar: User not enrolled, redirecting to courses page');
          navigate(`/courses/getCourses?courseId=${notification.courseId}`);
        }
      } catch (error) {
        console.error('Error checking enrollment:', error);
        navigate('/courses/getCourses');
      }
    } else if (notification.message.includes('repositorio')) {
      navigate('/admin/repository');
    }
  };


  const formatNotificationDate = (createdAt) => {
    const date = new Date(createdAt);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `Fecha de recibido ${day}/${month}/${year}`;
  };

  const parseNotificationTitle = (title) => {
    if (title.includes(' - ')) {
      const parts = title.split(' - ');
      return { category: parts[0], action: parts.slice(1).join(' - ') };
    }
    return { category: title, action: '' };
  };


  const handleConnectionRetry = async () => {
    try {
      await reconnect();
    } catch (error) {
      console.error('Error al reconectar:', error);
    }
  };

  const getUserInitials = (name) => {
    if (!name || typeof name !== "string") return "?";
    const formatted = formatUserDisplayName(name) || name.trim();
    const parts = formatted.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    if (parts.length === 1 && parts[0].length > 0) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return "?";
  };

  return (
    <>
      {toastNotification && (
        <NotificationCard
          notification={toastNotification}
          onClick={() => {
            setIsNotificationMenuOpen(true);
            dismissToast();
          }}
          onClose={() => {
            dismissToast();
          }}
        />
      )}

      <nav className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex justify-between items-center max-w-7xl mx-auto w-full px-4 py-3.5 md:px-6">
          <div className="flex gap-3 items-center min-w-0">
            {!isDesktop && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex flex-shrink-0 justify-center items-center w-10 h-10 text-gray-700 bg-white rounded-lg border border-gray-200 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:border-green-aqua hover:text-green-aqua focus:outline-none focus:ring-2 focus:ring-green-aqua focus:ring-offset-2"
              >
                {isMobileMenuOpen ? (
                  <XIcon className="w-5 h-5" />
                ) : (
                  <MenuIcon className="w-5 h-5" />
                )}
              </button>
            )}
            <img 
              src={mainLogo} 
              alt="Docentia" 
              className="h-10 object-contain transition-opacity duration-200 cursor-pointer md:h-12 hover:opacity-80 flex-shrink-0" 
              onClick={() => navigate("/")} 
            />
          </div>

        <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
          {/* Bloque unificado: usuario + acciones */}
          <div className="flex items-center overflow-visible rounded-2xl border border-gray-200/90 bg-white shadow-sm ring-1 ring-black/[0.03]">
            {usuarioConCedula?.id && (
              <>
                {/* Desktop: nombre y rol */}
                <div className="hidden min-h-[44px] max-w-[min(100%,14rem)] items-center gap-3 border-r border-gray-100 bg-gradient-to-r from-[#f8fafc] to-white px-3 py-2 sm:max-w-[16rem] md:flex lg:px-4">
                  <div
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#4DABA4] to-[#3d8f89] text-sm font-bold text-white shadow-inner ring-2 ring-white"
                    aria-hidden
                  >
                    {getUserInitials(usuarioConCedula.name || "")}
                  </div>
                  <div className="flex min-w-0 flex-col justify-center gap-0.5">
                    <span className="truncate text-sm font-semibold leading-tight text-gray-900">
                      {formatUserDisplayName(usuarioConCedula.name) || "Usuario"}
                    </span>
                    <span className="truncate text-xs font-medium leading-tight text-gray-500">
                      {getUserProfileLabel(usuarioConCedula)}
                    </span>
                  </div>
                </div>
                {/* Móvil: solo iniciales para no saturar */}
                <div className="flex min-h-[44px] items-center border-r border-gray-100 bg-gradient-to-br from-[#f8fafc] to-white px-2 py-1.5 md:hidden">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#4DABA4] to-[#3d8f89] text-xs font-bold text-white shadow-sm ring-2 ring-white"
                    title={formatUserDisplayName(usuarioConCedula.name) || "Usuario"}
                    aria-label={formatUserDisplayName(usuarioConCedula.name) || "Usuario"}
                  >
                    {getUserInitials(usuarioConCedula.name || "")}
                  </div>
                </div>
              </>
            )}

            <div className="flex min-h-[44px] items-center gap-0.5 px-1.5 py-2 sm:gap-1 sm:px-2">
          <Menu as="div" className="relative flex items-center justify-center">
            {({ open }) => {
              if (open !== isNotificationMenuOpen) {
                requestAnimationFrame(() => {
                  setIsNotificationMenuOpen(open);
                });
              }
              
              return (
                <>
                  <MenuButton className="relative flex h-9 w-9 shrink-0 -translate-y-2 items-center justify-center rounded-xl text-[#4DABA4] transition-all duration-200 hover:bg-[#4DABA4]/10 focus:outline-none focus:ring-2 focus:ring-[#4DABA4] focus:ring-offset-2">
                    <BellIcon className="w-[18px] h-[18px]" aria-hidden="true" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full min-w-[20px] h-[20px] border-2 border-white shadow-sm">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </MenuButton>
                  <MenuItems 
                    className="absolute top-full right-0 z-50 mt-2.5 w-[92vw] max-w-[520px] min-w-[320px] origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black/5 focus:outline-none max-h-[500px] overflow-hidden transition-all duration-300 ease-in-out"
                  >
              <div className="flex flex-col max-h-[500px]">
                <div className="flex relative justify-center items-center px-5 py-4 bg-white border-b border-gray-200">
                  <div className="flex absolute left-5 gap-2 items-center">
                    <button
                      onClick={() => setIsNotificationMenuOpen(false)}
                      className="flex justify-center items-center w-7 h-7 text-gray-600 rounded-lg transition-all duration-200 hover:bg-gray-100 active:scale-95"
                      aria-label="Cerrar"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <h3 className="text-base font-bold text-green-aqua">
                    Notificaciones
                  </h3>
                  {unreadCount > 0 && (
                    <div className="absolute right-5">
                      <button
                        onClick={() => markAllAsRead(userId)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-aqua bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg transition-all duration-200 hover:bg-white/30 active:scale-95"
                        aria-label="Marcar todas como leídas"
                      >
                        <CheckCheck className="w-3.5 h-3.5" />
                        <span>Marcar todas</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="overflow-y-auto flex-1">
                  {notificationsError && (
                    <div className="p-4 mx-4 mt-3 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg border border-red-200">
                      <div className="flex gap-3 items-start">
                        <div className="flex flex-shrink-0 justify-center items-center w-5 h-5 bg-red-500 rounded-full">
                          <span className="text-[10px] font-bold leading-none text-white">!</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="mb-1.5 text-sm font-semibold leading-tight text-red-800">
                            Error de conexión
                          </p>
                          <p className="mb-3 text-xs leading-relaxed text-red-600">
                            {notificationsError}
                          </p>
                          <button
                            onClick={handleConnectionRetry}
                            className="px-3 py-1.5 text-xs font-medium leading-tight text-red-700 bg-red-100 rounded-md transition-all duration-200 hover:bg-red-200 hover:scale-105 active:scale-95"
                          >
                            Reintentar conexión
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {notificationsLoading ? (
                    <div className="px-6 py-12 text-center">
                      <div className="mx-auto mb-4 w-12 h-12 rounded-full border-4 animate-spin border-green-aqua/20 border-t-green-aqua"></div>
                      <p className="text-sm font-semibold leading-tight text-gray-600">Cargando notificaciones...</p>
                      <p className="mt-1 text-xs text-gray-400">Espera un momento</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
                        <BellIcon className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-sm font-semibold leading-tight text-gray-600">No hay notificaciones</p>
                      <p className="mt-1 text-xs text-gray-400">Te notificaremos cuando lleguen nuevas</p>
                    </div>
                  ) : (
                    <div className="bg-white">
                      {notifications.slice(0, 10).map((notification, index) => {
                        const titleParts = parseNotificationTitle(stripHtml(notification.title || ''));
                        const cleanMessage = stripHtml(notification.message || '');
                        const cleanCourseName = stripHtml(notification.courseName || '');
                        const subtitle = cleanMessage || cleanCourseName;
                        const isUnread = !notification.isRead;
                        return (
                          <div key={notification.id} className="bg-white">
                            <button
                              onClick={() => handleNotificationClick(notification)}
                              className="w-full px-5 py-3.5 text-left transition-colors duration-200 bg-white hover:bg-gray-50"
                            >
                              <div className="flex gap-4 justify-between items-start">
                                <div className="flex-1 min-w-0">
                                  <div className="mb-1.5">
                                    <span className={`text-sm font-semibold ${
                                      isUnread ? 'text-green-aqua' : 'text-gray-400'
                                    }`}>
                                      {titleParts.category}
                                    </span>
                                    {titleParts.action && (
                                      <>
                                        <span className={`text-sm font-semibold mx-1 ${
                                          isUnread ? 'text-gray-900' : 'text-gray-400'
                                        }`}>
                                          -
                                        </span>
                                        <span className={`text-sm font-semibold ${
                                          isUnread ? 'text-gray-900' : 'text-gray-400'
                                        }`}>
                                          {titleParts.action}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  
                                  {subtitle && (
                                    <p className={`text-sm leading-relaxed ${
                                      isUnread ? 'text-gray-900' : 'text-gray-400'
                                    }`}>
                                      {subtitle}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="flex flex-col flex-shrink-0 gap-1.5 items-end">
                                  {isUnread && (
                                    <span className="px-2.5 py-1 text-xs font-medium text-white bg-green-aqua rounded whitespace-nowrap">
                                      Nueva
                                    </span>
                                  )}
                                  <span className={`text-xs whitespace-nowrap ${
                                    isUnread ? 'text-gray-500' : 'text-gray-400'
                                  }`}>
                                    {formatNotificationDate(notification.createdAt)}
                                  </span>
                                </div>
                              </div>
                            </button>
                            {index < notifications.slice(0, 10).length - 1 && (
                              <div className="h-px bg-gray-200"></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {notifications.length > 10 && (
                  <div className="px-5 py-3.5 bg-gradient-to-r from-gray-50/50 border-t border-gray-100 to-green-aqua/5">
                    <Link
                      to="/notifications"
                      className="flex items-center justify-center w-full px-4 py-2.5 text-sm font-semibold bg-white rounded-lg border shadow-sm transition-all duration-200 text-green-aqua border-green-aqua/20 hover:bg-green-aqua/10 hover:border-green-aqua/30 hover:shadow-md active:scale-98"
                    >
                      Ver todas las notificaciones ({notifications.length})
                    </Link>
                  </div>
                )}
              </div>
            </MenuItems>
                </>
              );
            }}
          </Menu>

          <Menu as="div" className="relative flex items-center justify-center">
            <MenuButton className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#4DABA4] transition-all duration-200 hover:bg-[#4DABA4]/10 focus:outline-none focus:ring-2 focus:ring-[#4DABA4] focus:ring-offset-2">
                <MoreVertical className="w-[18px] h-[18px]" />
              </MenuButton>
            <MenuItems className="absolute top-full right-0 z-40 mt-2 w-60 origin-top-right rounded-xl bg-white py-1.5 shadow-xl ring-1 ring-black/5 focus:outline-none">
              {isAdmin && (
                <>
                  <MenuItem>
                    <Link
                      to="/admin/main"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex gap-2.5 items-center px-4 py-2 w-full text-sm font-medium text-left text-[#4DABA4] transition-colors data-[focus]:bg-[#4DABA4]/10 hover:bg-[#4DABA4]/10"
                    >
                      <LayoutDashboard className="w-4 h-4" aria-hidden="true" />
                      Panel de administración
                    </Link>
                  </MenuItem>
                  <div className="my-1 h-px bg-gray-100" role="separator" />
                </>
              )}
              <MenuItem>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex gap-2.5 items-center px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors data-[focus]:bg-gray-100 hover:bg-gray-100"
                >
                  <LogOut className="w-4 h-4 text-gray-500" aria-hidden="true" />
                  Cerrar sesión
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
            </div>
          </div>
        </div>
        </div>
      </nav>

      {!withoutOutlet && (
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      )}
    </>
  );
};

export default Navbar;